import { useContext, useEffect } from 'react'
import { Link, useLocation, useParams } from 'react-router-dom'

import { classes, style } from 'typestyle'
import { get } from '../../../share/find'
import { withRouter } from '../../../contexts/with-router'

/* Contents ===================================================================================== */
import { GLOBAL_CONTENTS } from '../../../common/global-contents'

/* Hooks ======================================================================================== */
import useValue from '../../../hooks/use-value'
import useFetch from '../../../hooks/use-fetch'
import useLocal from '../../../hooks/use-local'
import { useTheme } from '../../../components/theme'

/* Components =================================================================================== */
import Right from '../../../../app-layouts/header/right'
import Skeleton from '../../../components/skeleton'
import ToolbarIcons from '../../../../app-layouts/toolbar/right'
import { Box } from '../../../components/box/box.component'
import { Button } from '../../../components/button'
import { Circle } from '../../../components/circle'
import { Dropdown } from '../../../components/dropdown'
import { Line } from '../../../components/line'
import { Loader } from '../../../components/loader'
import { Logo } from '../../../../components/logo'
import { Modal } from '../../../components/modal'
import { NavigationContext } from '../../../contexts/navigation'
import { Notification } from '../../../components/notification'
import { Text } from '../../../components/text/text.component'
import { Tooltip } from '../../../components/tooltip'

/* Styles ======================================================================================= */
import { f16i } from '../../../styles/styleset/font-size/f16i'
import { f20i } from '../../../styles/styleset/font-size/f20i'
import { hidesmi } from '../../../styles/styleset/hide/hidesmi'
import { ml12 } from '../../../styles/styleset/margin/ml12'
import { ml8 } from '../../../styles/styleset/margin/ml8'
import { mr12 } from '../../../styles/styleset/margin/mr12'
import { mr8 } from '../../../styles/styleset/margin/mr8'
import { p4 } from '../../../styles/styleset/padding/p4'
import { paddingVerticalOuter } from '../../../styles/default/padding'
import { pr8 } from '../../../styles/styleset/padding/pr8'
import { w100 } from '../../../styles/styleset/width/w100'
import { w100i } from '../../../styles/styleset/width/w100i'
import { pl0i } from '../../../styles/styleset/padding/pl0i'

/* <Toolbar /> ================================================================================== */
export const Toolbar: any = withRouter((props: any) => {
  const { context, row } = props
  const [local] = useLocal()
  const appLayoutContext: any = useContext(context)
  const location = useLocation()
  const params = useParams()
  const { groupId, parentRowId } = params
  const { navigation } = useContext(NavigationContext)
  const { toggle, visible } = appLayoutContext.components.navbar || {}

  const [currentTheme] = useTheme()

  const actions = get('payload.base.actions', navigation, 'array')

  const [isScrolled, $isScrolled] = useValue(false)
  const [bookmarks, $bookmarks] = useFetch(
    {},
    {
      url: `${process.env.REACT_APP_API_URL}/a/bookmarks`,
      search: {
        url: location.pathname,
      },
    },
  )

  useEffect(() => {
    if (bookmarks.mounted) {
      $bookmarks.get()
    }
  }, [bookmarks.mounted])

  useEffect(() => {
    const onScroll = () => {
      if (window.scrollY < 1) {
        $isScrolled.set(false)
      } else {
        $isScrolled.set(true)
      }
    }
    window.addEventListener('scroll', onScroll)

    return () => {
      window.removeEventListener('scroll', onScroll)
    }
  }, [])

  function getActions() {
    if (actions || props.actions) {
      return [
        ...[...actions, ...(props.actions || [])]
          .filter((a: any) => a.target !== 'table-row')
          .filter((a: any) => !a.hidden)
          .map((action: any) => {
            const result = {
              ...action,
              buttonProps: {
                baseline: action.primary ? 'primary' : 'surface',
                ...action.buttonProps,
              },
              to: (location: any) => {
                if (action.to[0] === '/') {
                  return `${action.to}`.replace('{{groupId}}', groupId || '')
                } else {
                  return `${location.pathname}/${`${action.to}`.replace(
                    '{{groupId}}',
                    groupId || '',
                  )}`
                }
              },
              icon: action.icon && (
                <i className={classes('material-icons-two-tone', f16i)}>{action.icon}</i>
              ),
            }

            if (action.type === 'request' || action.type === 'delete') {
              result.onClick = () => {
                Modal.pop()
                if (action.type === 'request' || action.type === 'delete') {
                  for (const key in params) {
                    action.to.url = action.to.url.replace(`{{${key}}}`, params[key])
                  }

                  const destination = location.pathname.split('/')
                  destination?.pop()
                  Modal.push({
                    type: 'Request',
                    title: action.description,
                    componentProps: {
                      to: action.to,
                      destination: action.link
                        ? action.link
                            .replace('{{groupId}}', groupId || '')
                            .replace('{{parentId}}', row?.parentRow?.[0] || '')
                        : (action.type === 'request' || action.type === 'delete') &&
                          action.target === 'row'
                        ? destination.join('/')
                        : null,
                    },
                  })
                }
              }
            }

            return result
          }),
      ]
    }

    return []
  }

  return (
    <Box
      baseline="surface"
      fullHeight
      fullWidth
      relative
      alignCenterY
      style={{ borderBottom: `1px solid ${currentTheme.boxes.border.medium}` }}
    >
      <Box
        baseline="none"
        className={classes(paddingVerticalOuter, visible && pl0i)}
        spaceBetween
        fullWidth
        alignCenterY
      >
        <Box flex fullHeight alignCenterY baseline="none">
          {!visible ? (
            <Tooltip label={GLOBAL_CONTENTS.openMenu[local]}>
              <Button
                icon={
                  <i className={classes(process.env.REACT_APP_GOOGLE_MATERIAL_ICONS, f20i)}>menu</i>
                }
                size="sm"
                baseline={'surface'}
                onClick={toggle}
                className={classes(pr8)}
              />
            </Tooltip>
          ) : (
            <Tooltip label={GLOBAL_CONTENTS.closeMenu[local]}>
              <Button
                icon={
                  <i className={classes(process.env.REACT_APP_GOOGLE_MATERIAL_ICONS, f20i)}>
                    menu_open
                  </i>
                }
                size="sm"
                baseline={'surface'}
                onClick={toggle}
                className={classes(pr8)}
              />
            </Tooltip>
          )}
          {!appLayoutContext.components.header.visible && (
            <Tooltip label={GLOBAL_CONTENTS.openPortal[local]}>
              <Button
                icon={<i className={classes('material-icons-two-tone', f20i)}>language</i>}
                className={classes(ml8, hidesmi)}
                size="sm"
                baseline="primary"
                onClick={() => {
                  Modal.push({
                    type: 'Portal',
                    title: (
                      <Box flex alignCenterY fullWidth spaceBetween>
                        <Box flex alignCenterY>
                          <Logo toggled={true} />
                          <Box minHeight={24} maxHeight={24} flex>
                            <Line horiz dark className={classes(ml12, mr12)} />
                          </Box>
                          <Text size="xl" disableMediaQuery>
                            {GLOBAL_CONTENTS.portal[local]}
                          </Text>
                        </Box>
                        <Button
                          label={GLOBAL_CONTENTS.close[local]}
                          baseline="background"
                          size="sm"
                          onClick={Modal.pop}
                          square
                        />
                      </Box>
                    ),
                    componentProps: {
                      onBookmarkDelete: () => {
                        $bookmarks.get()
                      },
                    },
                    closeOnOutsideClick: true,
                    position: 'top left',
                  })
                }}
              />
            </Tooltip>
          )}
          {getActions().filter((a: any) => !a.hidden).length > 0 && (
            <Box baseline="none" row alignCenterY className={classes(ml8, mr8)}>
              <Box baseline="none" row minHeight={18} fullHeight fullWidth>
                <Line horiz dark className={style({ height: 18 })} />
              </Box>
            </Box>
          )}
          {navigation.meta.isLoading ? (
            <>
              <Box baseline="none" row alignCenterY className={classes(ml8, mr8)}>
                <Box baseline="none" row minHeight={18} fullHeight fullWidth>
                  <Line horiz dark className={style({ height: 18 })} />
                </Box>
              </Box>
              <Skeleton width={80} height={30} />
            </>
          ) : (
            <>
              {getActions().filter((a: any) => !a.primary).length > 0 && (
                <Dropdown
                  floatProps={{
                    anchorPosition: 'bottom left',
                    dialogPosition: 'top left',
                    width: 120,
                  }}
                >
                  <Tooltip label={GLOBAL_CONTENTS.moreActions[local]}>
                    <Button
                      className={mr8}
                      icon={
                        <i className={classes(process.env.REACT_APP_GOOGLE_MATERIAL_ICONS, f20i)}>
                          more_horiz
                        </i>
                      }
                      baseline="background"
                      size="sm"
                    />
                  </Tooltip>
                  {() => {
                    return (
                      <Box
                        baseline="surface"
                        className={classes(p4, style({ width: 'fit-content' }))}
                        rounded
                        shadow
                        flex
                        row
                      >
                        {getActions()
                          .filter((a: any) => !a.primary)
                          .filter((a: any) => !a.hidden)
                          .map((action: any, key: any) => {
                            if (action.type === 'link') {
                              return (
                                <Link
                                  to={action.to(location)}
                                  target={action.newWindow ? '_blank' : undefined}
                                  key={key}
                                >
                                  <Button
                                    className={w100i}
                                    size="md"
                                    label={action.label}
                                    icon={action.icon}
                                    center={false}
                                    square
                                    {...action.buttonProps}
                                  />
                                </Link>
                              )
                            }

                            return (
                              <Button
                                className={w100}
                                size="md"
                                center={false}
                                label={action.label}
                                icon={action.icon}
                                onClick={action.onClick}
                                key={key}
                                square
                                {...action.buttonProps}
                              />
                            )
                          })}
                      </Box>
                    )
                  }}
                </Dropdown>
              )}
              {getActions()
                .filter((a: any) => a.primary)
                .filter((a: any) => !a.hidden)
                .map((action: any, key: any) => {
                  if (action.type === 'link') {
                    return (
                      <Link
                        className={mr8}
                        to={action.to(location)}
                        key={key}
                        target={action.newWindow}
                      >
                        <Button
                          size="sm"
                          label={action.label}
                          icon={action.icon}
                          {...action.buttonProps}
                          square
                        />
                      </Link>
                    )
                  }

                  return (
                    <Button
                      className={mr8}
                      size="sm"
                      label={action.label}
                      icon={action.icon}
                      onClick={action.onClick}
                      key={key}
                      square
                      {...action.buttonProps}
                    />
                  )
                })}
            </>
          )}
        </Box>
        <Box className={ml8} baseline="none" fullHeight flex alignRight alignCenterY>
          <ToolbarIcons />
          {!appLayoutContext.components.header.visible && <Right />}
          <Loader
            skeleton={
              <Circle className={classes(style({ width: 28, height: 28 }))}>
                <Box minWidth={28} minHeight={28} alignCenterY alignCenterX baseline="none">
                  <Skeleton variant="circle" width={28} height={28} />
                </Box>
              </Circle>
            }
            component={
              <Tooltip
                label={
                  Object.keys(bookmarks.payload).length
                    ? GLOBAL_CONTENTS.bookmarkRemove[local]
                    : GLOBAL_CONTENTS.bookmarkAdd[local]
                }
              >
                <Button
                  size="sm"
                  baseline={Object.keys(bookmarks.payload).length ? 'background' : 'surface'}
                  icon={
                    <i className={classes(f20i, 'material-icons-two-tone')}>
                      {Object.keys(bookmarks.payload).length ? 'bookmark_added' : 'bookmark_add'}
                    </i>
                  }
                  ariaLabel={
                    Object.keys(bookmarks.payload).length
                      ? GLOBAL_CONTENTS.bookmarkRemove[local]
                      : GLOBAL_CONTENTS.bookmarkAdd[local]
                  }
                  border={false}
                  onClick={() => {
                    $bookmarks
                      .post({
                        body: { url: location.pathname, title: props.title, groupId, parentRowId },
                      })
                      .then((payload: any) => {
                        if (Object.keys(payload).length) {
                          Notification.push({
                            label: GLOBAL_CONTENTS.bookmarkAdded[local],
                          })
                        } else {
                          Notification.push({
                            label: GLOBAL_CONTENTS.bookmarkRemoved[local],
                          })
                        }
                      })
                  }}
                />
              </Tooltip>
            }
            timeout={300}
            loaded={bookmarks.mounted && !bookmarks.meta.isLoading}
          />
          <Box baseline="none" row alignCenterY className={classes(ml8, mr8)}>
            <Box baseline="none" row minHeight={18} fullWidth>
              <Line horiz dark className={style({ height: 18 })} />
            </Box>
          </Box>
          <Tooltip
            label={
              appLayoutContext.components.header.visible
                ? GLOBAL_CONTENTS.closeHeader[local]
                : GLOBAL_CONTENTS.openHeader[local]
            }
          >
            <Button
              baseline={appLayoutContext.components.header.visible ? 'surface' : 'background'}
              icon={
                <span className={classes(process.env.REACT_APP_GOOGLE_MATERIAL_ICONS, f20i)}>
                  {appLayoutContext.components.header.visible
                    ? 'keyboard_arrow_up'
                    : 'keyboard_arrow_down'}
                </span>
              }
              size="sm"
              onClick={() => {
                appLayoutContext.components.header.toggle()
              }}
            />
          </Tooltip>
        </Box>
      </Box>
    </Box>
  )
})
